import { useController } from "react-hook-form";
import { INPUT_TYPES } from "@miplatform/constants";
import { TextField } from "@mui/material";

/**
 * A controlled text field component that integrates with React Hook Form
 *
 * @param {Object} props - The component props
 * @param {string} [props.name=""] - The name of the form field
 * @param {Object} [props.formControl={}] - React Hook Form control object
 * @param {Object} [props.rules={}] - Validation rules for the field
 * @param {string} [props.type="text"] - The input type (text, email, password, etc.)
 * @param {Function} [props.valueModifier] - Function to modify the input value before updating form state
 * @param {Function} [props.handleChange] - Custom change handler that overrides the default behavior
 * @param {string} [props.value] - Controlled value that overrides the form field value
 * @param {string} [props.placeholder] - Placeholder text for the input
 * @param {string} [props.label] - Label text for the input field
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {Object} [props.inputProps] - Additional props to pass to the underlying input element
 * @param {Object} [props.sx={}] - Material-UI sx prop for custom styling
 * @param {string|null} [props.helperText=null] - Helper text to display below the input
 * @param {boolean} [props.readOnly] - Whether the input is read-only
 * @param {boolean} [props.required] - Whether the input is required
 * @param {boolean} [props.whiteBackground] - Whether to apply a white background style
 * @param {boolean} [props.multiline] - Whether to apply a multiline style
 * @param {number} [props.rows] - Number of rows for multiline input
 * @param {Object} [props.rest] - Additional props passed to the TextField component
 * @param {Function} [props.onKeyDown] - Custom key down handler for the input
 * @param {Function} [props.onInput] - Custom input handler for the input
 * @param {string} [props.className] - Additional class name for the TextField component
 * @returns {JSX.Element} A Material-UI TextField component with form integration
 */
export function MiControlledTextField({
  name = "",
  formControl: control = {},
  rules = {},
  type = "text",
  valueModifier,
  handleChange,
  value,
  placeholder = "--",
  label,
  disabled,
  inputProps,
  sx = {},
  helperText = null,
  readOnly,
  whiteBackground,
  multiline,
  rows,
  required,
  maxLength,
  ...rest
}) {
  const {
    field: { onChange, onBlur, value: defaultValue, ref },
    fieldState: { error },
  } = useController({ name, control, rules });

  const customOnChange = (event) => {
    const { value: inputValue } = event.target;
    onChange(valueModifier ? valueModifier(inputValue) : event);
  };

  const isShrink = !!readOnly || type === INPUT_TYPES.TIME;
  const placeholderText = !readOnly ? "" : placeholder;

  return (
    <TextField
      inputRef={ref}
      value={value ?? defaultValue ?? ""}
      onChange={handleChange || customOnChange}
      onBlur={onBlur}
      error={!!error && !readOnly}
      helperText={error ? error.message : helperText}
      size="small"
      type={type}
      readOnly={readOnly}
      fullWidth
      disabled={disabled}
      placeholder={placeholderText}
      label={label}
      sx={{
        ...sx,
        ...(!!error && {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "error.main",
          },
          "& .MuiInputLabel-root": {
            color: "error.main",
          },
          "& .MuiFormHelperText-root": {
            color: (theme) => theme.palette.error.main,
          },

          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) => theme.palette.error.main,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) => theme.palette.error.main,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) => theme.palette.error.main,
            },
          },
        }),
        ...(whiteBackground && { backgroundColor: "white" }),

        "& .MuiInputBase-input": {
          textOverflow: "ellipsis",
        },
      }}
      multiline={multiline}
      rows={rows}
      required={required}
      slotProps={{
        htmlInput: {
          maxLength,
          "data-testid": `mi-input-text-box-${name}`,
          ...inputProps,
          readOnly,
        },
        inputLabel: isShrink
          ? {
              shrink: true,
            }
          : {},
      }}
      {...rest}
    />
  );
}
